import type {
  ChatbotEvalMode,
  LiveEvalFinding,
  LiveTrafficMessage,
  LiveTrafficTurn,
} from '@borradh-workspace/api-client/types';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  KeyRound,
  Pause,
  Play,
  Radio,
  RefreshCw,
  Search,
  ShieldAlert,
  Sparkles,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { useEvaluateLiveMessages, useLiveTraffic } from '../../api';
import { OrgCombobox, type SelectedOrg } from '../org-combobox';
import {
  CHANNEL_LABELS,
  InboxBubble,
  InboxChip,
  InboxEvent,
  InboxPane,
  InboxRail,
  InboxRailItem,
  InboxSplit,
  InboxStats,
  InboxSystem,
  InboxThread,
  Linkify,
  channelLabel,
  shortTime,
} from './inbox';
import { LiveMonitorCard } from './live-monitor-card';

/** One scoring request covers at most this many replies (the API's cap). */
const BATCH = 10;

const WINDOWS = [
  { value: '1', label: 'Last hour' },
  { value: '6', label: 'Last 6 hours' },
  { value: '24', label: 'Last 24 hours' },
  { value: '168', label: 'Last 7 days' },
];

const MODE_LABELS: Record<ChatbotEvalMode, string> = {
  invented_fact: 'Invented fact',
  cross_branch_price: 'Wrong branch price',
  wrong_service: 'Wrong service',
  ignored_question: 'Ignored question',
  repetition_loop: 'Repetition',
  over_pushy: 'Too pushy',
  no_escalation: 'No handoff',
  stalled: 'Stalled',
  broken_artifact: 'Broken artifact',
  duplicate_send: 'Duplicate send',
  tone_mismatch: 'Tone',
};

const GUARD_LABELS: Record<string, string> = {
  grounding_violation: 'Grounding blocked',
  rule_violation: 'Rule broken',
  suspicious_claim: 'Suspicious claim',
  fabrication_detected: 'Fabrication caught',
  fabrication_overruled: 'Fabrication overruled',
  review_retry: 'Rewritten',
  review_exhausted: 'Review gave up',
  rounds_exhausted: 'Out of tool rounds',
  tool_failed: 'Tool failed',
  tool_rejected: 'Tool rejected',
  delivery_failed: 'Delivery failed',
  escalated: 'Handed off',
};

type Filter = 'all' | 'flagged' | 'unchecked' | 'guards';

const SEVERITY_CHIP: Record<LiveEvalFinding['severity'], string> = {
  high: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20',
  medium:
    'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
  low: 'bg-muted text-muted-foreground',
};

function Verdict({
  message,
  pending,
}: {
  message: LiveTrafficMessage;
  pending: boolean;
}) {
  if (pending) return <Spinner className="size-4 text-muted-foreground" />;
  const verdict = message.evaluation?.verdict;
  if (verdict === 'pass') {
    return (
      <CheckCircle2
        aria-label="Passed"
        className="size-4 text-emerald-600 dark:text-emerald-400"
      />
    );
  }
  if (verdict === 'flag') {
    return (
      <XCircle
        aria-label="Flagged"
        className="size-4 text-rose-600 dark:text-rose-400"
      />
    );
  }
  if (verdict === 'error') {
    return (
      <AlertTriangle
        aria-label="Could not check"
        className="size-4 text-amber-600 dark:text-amber-400"
      />
    );
  }
  return (
    <CircleDashed
      aria-label="Not checked"
      className="size-4 text-muted-foreground"
    />
  );
}

/** The reply with each finding's quote marked, first occurrence only. */
function HighlightedReply({
  text,
  findings,
}: {
  text: string;
  findings: LiveEvalFinding[];
}) {
  const ranges = findings
    .map((finding) => {
      const start = text.indexOf(finding.quote);
      return start === -1
        ? null
        : { start, end: start + finding.quote.length, finding };
    })
    .filter((range): range is NonNullable<typeof range> => range !== null)
    .sort((a, b) => a.start - b.start)
    .filter(
      (range, index, all) => index === 0 || range.start >= all[index - 1].end
    );

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) {
      parts.push(
        <Linkify key={`t${cursor}`} text={text.slice(cursor, range.start)} />
      );
    }
    parts.push(
      <mark
        key={range.start}
        className={cn(
          'rounded px-0.5 text-inherit',
          range.finding.severity === 'high'
            ? 'bg-rose-500/20'
            : 'bg-amber-500/20'
        )}
      >
        {text.slice(range.start, range.end)}
      </mark>
    );
    cursor = range.end;
  }
  parts.push(<Linkify key={`t${cursor}`} text={text.slice(cursor)} />);
  return <>{parts}</>;
}

/**
 * The channel filter: every platform, plus iMessage (SMS conversations
 * LoopMessage carried over iMessage). Test chat keeps the plain platforms.
 */
const LIVE_CHANNEL_FILTERS: Record<string, string> = {
  ...CHANNEL_LABELS,
  imessage: 'iMessage',
};

type Conversation = {
  id: string;
  customerName: string | null;
  organizationName: string;
  platform: string;
  transport: LiveTrafficMessage['transport'];
  status: string;
  /** Claire's replies in the window, oldest first. */
  replies: LiveTrafficMessage[];
  thread?: LiveTrafficTurn[];
  lastAt: string;
  flagged: number;
  unchecked: number;
  guards: number;
  undelivered: number;
};

/** The feed is one row per reply; the page reads it one row per customer. */
function groupConversations(
  messages: LiveTrafficMessage[],
  selected?: {
    id: string;
    customerName: string | null;
    organizationName: string;
    platform: string;
    transport: LiveTrafficMessage['transport'];
    status: string;
    lastAt: string;
    thread: LiveTrafficTurn[];
  }
): Conversation[] {
  const byId = new Map<string, Conversation>();
  for (const message of messages) {
    let conversation = byId.get(message.conversationId);
    if (!conversation) {
      conversation = {
        id: message.conversationId,
        customerName: message.customerName ?? null,
        organizationName: message.organizationName,
        platform: message.platform,
        transport: message.transport,
        status: message.conversationStatus,
        replies: [],
        lastAt: message.at,
        flagged: 0,
        unchecked: 0,
        guards: 0,
        undelivered: 0,
      };
      byId.set(message.conversationId, conversation);
    }
    conversation.replies.push(message);
    if (message.at > conversation.lastAt) conversation.lastAt = message.at;
    if (message.evaluation?.verdict === 'flag') conversation.flagged += 1;
    if (!message.evaluation) conversation.unchecked += 1;
    if (message.guardEvents.length) conversation.guards += 1;
    if (message.delivery === 'failed') conversation.undelivered += 1;
  }
  if (selected) {
    const existing = byId.get(selected.id);
    if (existing) existing.thread = selected.thread;
    else
      byId.set(selected.id, {
        ...selected,
        replies: [],
        flagged: 0,
        unchecked: 0,
        guards: 0,
        undelivered: 0,
      });
  }
  const conversations = [...byId.values()];
  for (const conversation of conversations) {
    conversation.replies.sort((a, b) => a.at.localeCompare(b.at));
  }
  return conversations.sort((a, b) => b.lastAt.localeCompare(a.lastAt));
}

type ThreadEntry =
  | { kind: 'reply'; at: string; message: LiveTrafficMessage }
  | { kind: 'turn'; at: string; turn: LiveTrafficTurn };

/**
 * The transcript, rebuilt from each reply and the turns before it. A turn that
 * is also a reply in the window is shown once, as the reply.
 */
function buildThread(conversation: Conversation): ThreadEntry[] {
  const key = (at: string, role: string, text: string) =>
    `${at}|${role}|${text}`;
  const entries = new Map<string, ThreadEntry>();
  for (const message of conversation.replies) {
    entries.set(key(message.at, 'bot', message.text), {
      kind: 'reply',
      at: message.at,
      message,
    });
  }
  for (const message of conversation.replies) {
    for (const turn of message.context) {
      const id = key(turn.at, turn.role, turn.text);
      if (!entries.has(id))
        entries.set(id, { kind: 'turn', at: turn.at, turn });
    }
  }
  for (const turn of conversation.thread ?? []) {
    const id = key(turn.at, turn.role, turn.text);
    if (!entries.has(id)) entries.set(id, { kind: 'turn', at: turn.at, turn });
  }
  return [...entries.values()].sort((a, b) => a.at.localeCompare(b.at));
}

function matches(conversation: Conversation, query: string) {
  if (!query) return true;
  const needle = query.toLowerCase();
  const hay = [
    conversation.customerName ?? '',
    conversation.organizationName,
    ...conversation.replies.flatMap((reply) => [
      reply.text,
      ...reply.context.map((turn) => turn.text),
    ]),
  ];
  return hay.some((text) => text.toLowerCase().includes(needle));
}

function ReplyEntry({
  message,
  modes,
  pending,
  canEvaluate,
  onEvaluate,
  onContinue,
}: {
  message: LiveTrafficMessage;
  modes: Record<string, string>;
  pending: boolean;
  canEvaluate: boolean;
  onEvaluate: (force: boolean) => void;
  onContinue?: () => void;
}) {
  const evaluation = message.evaluation;
  const findings = evaluation?.findings ?? [];
  return (
    <div data-testid="live-message" className="flex flex-col items-end gap-1.5">
      <InboxBubble
        from="bot"
        time={shortTime(message.at)}
        onContinue={onContinue}
        continueLabel="Continue in Test chat"
        className={cn(
          evaluation?.verdict === 'flag' && 'ring-1 ring-rose-500/40',
          evaluation?.verdict === 'error' && 'ring-1 ring-amber-500/40'
        )}
      >
        <HighlightedReply text={message.text} findings={findings} />
      </InboxBubble>

      <div className="flex flex-wrap items-center justify-end gap-2 text-muted-foreground text-xs">
        <Verdict message={message} pending={pending} />
        <span>
          {evaluation?.verdict === 'pass'
            ? 'Passed the evals'
            : evaluation?.verdict === 'flag'
              ? `${findings.length} finding${findings.length === 1 ? '' : 's'}`
              : evaluation?.verdict === 'error'
                ? 'Could not check'
                : 'Not checked yet'}
        </span>
        {message.delivery === 'failed' ? (
          <InboxChip tone="warn">Not delivered</InboxChip>
        ) : null}
        {canEvaluate ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-7"
            disabled={pending}
            onClick={() => onEvaluate(Boolean(evaluation))}
          >
            <Sparkles />
            {evaluation ? 'Re-check' : 'Check'}
          </Button>
        ) : null}
      </div>

      {findings.map((finding) => (
        <InboxEvent
          key={`${finding.mode}-${finding.quote}`}
          tone={finding.severity === 'low' ? 'warn' : 'flag'}
        >
          <span className="mr-1.5 inline-flex items-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  variant="outline"
                  className={SEVERITY_CHIP[finding.severity]}
                >
                  {MODE_LABELS[finding.mode] ?? finding.mode}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>{modes[finding.mode]}</TooltipContent>
            </Tooltip>
            <span className="capitalize opacity-80">{finding.severity}</span>
          </span>
          <span>{finding.why}</span>
        </InboxEvent>
      ))}

      {evaluation?.verdict === 'error' ? (
        <InboxEvent tone="warn">{evaluation.error}</InboxEvent>
      ) : null}

      {message.guardEvents.map((event) => (
        <InboxEvent key={`${event.event}-${event.at}`} tone="tool">
          <span className="inline-flex items-center gap-1.5 font-medium">
            <ShieldAlert className="size-3.5" />
            {GUARD_LABELS[event.event] ?? event.event}
          </span>
          <span className="opacity-70"> · {shortTime(event.at)}</span>
          {event.detail ? (
            <span className="mt-0.5 block text-foreground/80">
              {event.detail}
            </span>
          ) : null}
        </InboxEvent>
      ))}
    </div>
  );
}

function ConversationPane({
  conversation,
  modes,
  pending,
  canEvaluate,
  isEvaluating,
  evaluate,
  onContinue,
}: {
  conversation: Conversation;
  modes: Record<string, string>;
  pending: Set<string>;
  canEvaluate: boolean;
  isEvaluating: boolean;
  evaluate: (input: { messageIds: string[]; force?: boolean }) => void;
  /** Hosted Harness: continue this real conversation as a test from `at`. */
  onContinue?: (conversationId: string, at: string) => void;
}) {
  const continueAt = (at: string) =>
    onContinue ? () => onContinue(conversation.id, at) : undefined;
  const thread = buildThread(conversation);
  const unchecked = conversation.replies
    .filter((reply) => !reply.evaluation)
    .map((reply) => reply.id);
  return (
    <InboxPane
      title={conversation.customerName ?? 'Unknown customer'}
      meta={[
        conversation.organizationName,
        channelLabel(conversation.platform, conversation.transport),
        conversation.status.replace(/_/g, ' '),
        `${conversation.replies.length} ${conversation.replies.length === 1 ? 'reply' : 'replies'} in this window`,
      ].join(' · ')}
      actions={
        canEvaluate && unchecked.length ? (
          <Button
            size="sm"
            variant="outline"
            disabled={isEvaluating}
            onClick={() => evaluate({ messageIds: unchecked.slice(0, BATCH) })}
          >
            <Sparkles />
            Check this conversation
          </Button>
        ) : null
      }
    >
      <InboxThread>
        {thread.map((entry) => {
          if (entry.kind === 'reply') {
            return (
              <ReplyEntry
                key={entry.message.id}
                message={entry.message}
                modes={modes}
                pending={pending.has(entry.message.id)}
                canEvaluate={canEvaluate}
                onEvaluate={(force) =>
                  evaluate({ messageIds: [entry.message.id], force })
                }
                onContinue={continueAt(entry.message.at)}
              />
            );
          }
          const { turn } = entry;
          const id = `${turn.at}-${turn.role}-${turn.text.slice(0, 24)}`;
          if (turn.role === 'system') {
            return <InboxSystem key={id}>{turn.text}</InboxSystem>;
          }
          return (
            <InboxBubble
              key={id}
              from={turn.role}
              time={shortTime(turn.at)}
              onContinue={continueAt(turn.at)}
              continueLabel="Continue in Test chat"
            >
              <Linkify text={turn.text} />
            </InboxBubble>
          );
        })}
      </InboxThread>
    </InboxPane>
  );
}

export function LiveTrafficPanel({
  onContinue,
  initialConversationId,
}: {
  initialConversationId?: string;
  /** Hosted Harness: continue a real conversation as a test, from a point. */
  onContinue?: (conversationId: string, at: string) => void;
} = {}) {
  const [windowHours, setWindowHours] = useState('24');
  const [platform, setPlatform] = useState<string>('all');
  const [org, setOrg] = useState<SelectedOrg | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [paused, setPaused] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(
    initialConversationId ?? null
  );

  useEffect(() => {
    setSelectedId(initialConversationId ?? null);
  }, [initialConversationId]);

  const { feed, messages, isLoading, isFetching, error, updatedAt, refetch } =
    useLiveTraffic(
      {
        conversationId: initialConversationId,
        windowHours: Number(windowHours),
        platform: platform === 'all' ? null : platform,
        organizationId: org?.id ?? null,
      },
      { paused }
    );
  const { evaluate, isEvaluating, pendingIds } = useEvaluateLiveMessages();
  const pending = useMemo(() => new Set(pendingIds), [pendingIds]);
  const canEvaluate = Boolean(feed?.evaluatorAvailable);

  const conversations = useMemo(
    () => groupConversations(messages, feed?.selectedConversation),
    [messages, feed?.selectedConversation]
  );
  const unchecked = messages.filter((message) => !message.evaluation);
  const checked = messages.filter(
    (message) => message.evaluation && message.evaluation.verdict !== 'error'
  );
  const flagged = messages.filter(
    (message) => message.evaluation?.verdict === 'flag'
  );
  const withGuards = messages.filter(
    (message) => message.guardEvents.length > 0
  );
  const failedDelivery = messages.filter(
    (message) => message.delivery === 'failed'
  );

  const shown = conversations.filter(
    (conversation) =>
      (filter === 'all' ||
        (filter === 'flagged' && conversation.flagged > 0) ||
        (filter === 'unchecked' && conversation.unchecked > 0) ||
        (filter === 'guards' && conversation.guards > 0)) &&
      matches(conversation, query.trim())
  );
  const selected =
    shown.find((conversation) => conversation.id === selectedId) ??
    (initialConversationId ? null : shown[0]) ??
    null;

  const checkNext = () => {
    const ids = unchecked.slice(0, BATCH).map((message) => message.id);
    if (ids.length) evaluate({ messageIds: ids });
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="size-4" />
        <AlertTitle>Couldn’t read live traffic</AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <LiveMonitorCard onShowFlagged={() => setFilter('flagged')} />

      <div className="flex flex-wrap items-center gap-3">
        <span
          className={cn(
            'inline-flex items-center gap-2 rounded-full border px-3 py-1 font-medium text-xs',
            paused
              ? 'text-muted-foreground'
              : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
          )}
        >
          <span className="relative flex size-2">
            {paused ? null : (
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-500 opacity-60" />
            )}
            <span
              className={cn(
                'relative inline-flex size-2 rounded-full',
                paused ? 'bg-muted-foreground' : 'bg-emerald-500'
              )}
            />
          </span>
          {paused ? 'Paused' : 'Watching production'}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setPaused((value) => !value)}
        >
          {paused ? <Play /> : <Pause />}
          {paused ? 'Resume' : 'Pause'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void refetch()}
          disabled={isFetching}
          aria-label="Refresh now"
        >
          <RefreshCw className={cn(isFetching && 'animate-spin')} />
        </Button>
        {updatedAt ? (
          <span className="text-muted-foreground text-xs">
            Updated{' '}
            {formatDistanceToNowStrict(new Date(updatedAt), {
              addSuffix: true,
            })}
          </span>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          disabled={!canEvaluate || !unchecked.length || isEvaluating}
          onClick={checkNext}
          title="One reviewer call per reply. Verdicts are saved to the conversation’s audit trail, so each reply is scored once."
        >
          {isEvaluating ? <Spinner /> : <Sparkles />}
          {unchecked.length
            ? `Check next ${Math.min(BATCH, unchecked.length)} of ${unchecked.length}`
            : 'All checked'}
        </Button>
      </div>

      {feed && !canEvaluate ? (
        <Alert>
          <KeyRound className="size-4" />
          <AlertTitle>Checking is off on this API</AlertTitle>
          <AlertDescription>
            Replies still stream in, but this API has no OpenAI key, so they
            can’t be scored against the evals.
          </AlertDescription>
        </Alert>
      ) : null}

      {isLoading ? null : (
        <InboxStats
          items={[
            { label: 'conversations', value: conversations.length },
            { label: 'replies', value: messages.length },
            { label: 'checked', value: checked.length },
            {
              label: checked.length
                ? `flagged (${Math.round((flagged.length / checked.length) * 100)}%)`
                : 'flagged',
              value: flagged.length,
              tone: flagged.length ? 'text-rose-600 dark:text-rose-400' : '',
            },
            { label: 'guard hits', value: withGuards.length },
            {
              label: 'not delivered',
              value: failedDelivery.length,
              tone: failedDelivery.length
                ? 'text-rose-600 dark:text-rose-400'
                : '',
            },
          ]}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-[1_1_220px]">
          <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 size-4 text-muted-foreground" />
          <Input
            type="search"
            aria-label="Search conversations"
            placeholder="Search names, clinics, message text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={windowHours} onValueChange={setWindowHours}>
          <SelectTrigger aria-label="Window" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOWS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger aria-label="Channel" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All channels</SelectItem>
            {Object.entries(LIVE_CHANNEL_FILTERS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <OrgCombobox value={org} onChange={setOrg} className="w-56" />
          {org ? (
            <Button
              size="icon"
              variant="ghost"
              aria-label="All clinics"
              onClick={() => setOrg(null)}
            >
              <X />
            </Button>
          ) : null}
        </div>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={filter}
          onValueChange={(value) => value && setFilter(value as Filter)}
        >
          <ToggleGroupItem value="all">All</ToggleGroupItem>
          <ToggleGroupItem value="flagged">Flagged</ToggleGroupItem>
          <ToggleGroupItem value="unchecked">Not checked</ToggleGroupItem>
          <ToggleGroupItem value="guards">Guard hits</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {isLoading ? (
        <InboxSplit rail={<Skeleton className="h-96 w-full" />}>
          <Skeleton className="h-96 w-full" />
        </InboxSplit>
      ) : shown.length && selected ? (
        <InboxSplit
          rail={
            <InboxRail label="Conversations">
              {shown.map((conversation) => (
                <InboxRailItem
                  key={conversation.id}
                  testId="live-conversation"
                  title={conversation.customerName ?? 'Unknown customer'}
                  time={shortTime(conversation.lastAt)}
                  subtitle={conversation.organizationName}
                  active={conversation.id === selected.id}
                  onSelect={() => setSelectedId(conversation.id)}
                  chips={
                    <>
                      <InboxChip>
                        {channelLabel(
                          conversation.platform,
                          conversation.transport
                        )}
                      </InboxChip>
                      <InboxChip>
                        {conversation.replies.length}{' '}
                        {conversation.replies.length === 1
                          ? 'reply'
                          : 'replies'}
                      </InboxChip>
                      {conversation.flagged ? (
                        <InboxChip tone="warn">
                          {conversation.flagged} flagged
                        </InboxChip>
                      ) : null}
                      {conversation.guards ? (
                        <InboxChip tone="tool">
                          {conversation.guards} guard
                          {conversation.guards === 1 ? '' : 's'}
                        </InboxChip>
                      ) : null}
                      {conversation.status === 'agent_handling' ? (
                        <InboxChip tone="agent">Handed off</InboxChip>
                      ) : null}
                      {conversation.undelivered ? (
                        <InboxChip tone="warn">Not delivered</InboxChip>
                      ) : null}
                    </>
                  }
                />
              ))}
            </InboxRail>
          }
        >
          <ConversationPane
            conversation={selected}
            modes={feed?.modes ?? {}}
            pending={pending}
            canEvaluate={canEvaluate}
            isEvaluating={isEvaluating}
            evaluate={evaluate}
            onContinue={onContinue}
          />
        </InboxSplit>
      ) : (
        <Empty className="border border-dashed py-14">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Radio />
            </EmptyMedia>
            <EmptyTitle>
              {messages.length
                ? 'Nothing matches this filter'
                : 'No replies in this window'}
            </EmptyTitle>
            <EmptyDescription>
              {messages.length
                ? 'Clear the search or switch the filter back to All to see every conversation.'
                : 'Claire hasn’t replied to a customer here yet. New replies appear within ten seconds.'}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
    </div>
  );
}
